import React from "react";
import { useTranslation } from "react-i18next";
import {
    Grid,
    Filter,
    useBaseAppContext,
    useResourceApi,
    useFormContext,
    ActionButton,
    useFilterContext,
    numberFormatCurrency,
    numberFormat,
} from "@programari-limit/base-react";
import {
    Box,
    Divider,
    darken,
    Icon,
    IconButton,
    Tooltip,
    Typography,
    Badge,
} from "@mui/material";
import GridFilterField from "components/GridFilterField";
import MuiGrid from "@mui/material/Grid";
import { SpringFilterQueryBuilder } from "utils/spring-filter-query-builder";
import {
    columnesArticle,
    columnesUsuaris,
} from "sharedAdvancedSearch/advancedSearchColumns";
import GridFormField from "components/GridFormField";
import { getFieldHighlightRed, getFielYellow } from "pages/reserves/Reserves";
import { suffixValueFormatter } from "utils/muiUtils";
import CellEditComp2 from "components/CellEditComp2";
import { GRID_CHECKBOX_SELECTION_FIELD } from "@mui/x-data-grid";
import WarningIcon from "@mui/icons-material/Warning";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { likeAny } from "utils/springFilterUtils";
import { useNavigate } from "react-router";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

const ActionAbonarContent = (props) => {
    const { operacio } = props;
    const { t } = useTranslation();
    const { data } = useFormContext();

    const columns = [
        {
            name: "articleNom",
            flex: 1,
        },
        {
            name: "importRestant",
            flex: 0.75,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "preu",
            flex: 0.75,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "quantitatRestant",
            flex: 0.75,
        },
        {
            name: "devolucio",
            flex: 1,
            minWidth: 220,
            headerName: t("operacio.action.campAbonament"),
            renderCell: (params) => (
                <CellEditComp2
                    tootlip={t("operacio.action.tooltipCampAbonament")}
                    placeholder={t("operacio.action.campAbonament")}
                    id={params.id}
                    keyVal="quantitatsLinia"
                    max={params?.row?.quantitatRestant}
                    min={0}
                    step={0.001}
                />
            ),
        },
    ];
    return (
        <Box sx={{ height: "100%" }}>
            <MuiGrid container spacing={2} sx={{ my: 1 }}>
                <GridFormField
                    name="user"
                    lg={6}
                    advancedSearchColumns={columnesUsuaris}
                    advancedSearchMaxWidth="lg"
                    size="small"
                />
                <GridFormField name="tipusAbonamentForm" lg={3} size="small" />
                <GridFormField name="pagamentTipus" lg={3} size="small" />
                <GridFormField
                    name="importTargeta"
                    lg={3}
                    size="small"
                    disabled={data?.pagamentTipus === "EFECTIU"}
                    suffix={" €"}
                />
                <GridFormField
                    name="importEfectiu"
                    lg={3}
                    size="small"
                    disabled={data?.pagamentTipus === "TARGETA"}
                    suffix={" €"}
                />
                <GridFormField
                    name="importAbonament"
                    lg={3}
                    size="small"
                    suffix={" €"}
                    disabled
                />
                <GridFormField
                    name="importARetornar"
                    lg={3}
                    size="small"
                    suffix={" €"}
                    disabled
                />
            </MuiGrid>
            {data?.tipusAbonamentForm === "ABONAMENT_PARCIAL" && (
                <Grid
                    title={t("operacio.linies.title")}
                    resourceName="operacioLinia"
                    columns={columns}
                    readOnly
                    inlineEditable={false}
                    density="compact"
                    fixedFilter={`operacio.id : '${operacio?.id}'`}
                    height={300}
                />
            )}
        </Box>
    );
};

const FilterContent = (props) => {
    const { filterApiRef } = props;
    const { t } = useTranslation();
    const { data } = useFilterContext();

    return (
        <MuiGrid container spacing={2}>
            <GridFilterField name="tipusOperacio" md={6} lg={2} />
            <GridFilterField name="dataInici" xs={6} md={6} lg={2} />
            <GridFilterField name="dataFi" xs={6} md={6} lg={2} />
            <GridFilterField
                name="article"
                md={6}
                lg={3}
                advancedSearchColumns={columnesArticle}
                advancedSearchMaxWidth={"lg"}
            />
            <GridFilterField name="recurs" md={6} lg={3} />
            <GridFilterField name="embarcacio" md={6} lg={3} />
            <GridFilterField name="estatEveret" sm={5} md={5} lg={3} xl={2.5} />
            <GridFilterField name="numero" xs={8} sm={5} md={5} lg={3} />

            <Tooltip title="Error Everet">
                <MuiGrid item xs={0.5}>
                    <GridFilterField
                        name="errorEveret"
                        label=""
                        // title="Error Everet"
                        xs={0.5}
                        icon={<WarningAmberIcon />}
                        checkedIcon={<WarningIcon />}
                        color={data?.errorEveret ? "error" : "primary"}
                    />
                </MuiGrid>
            </Tooltip>
            <MuiGrid item lg={0.5}>
                <Tooltip title={t("comu.resetFiltre")}>
                    <IconButton
                        onClick={() => filterApiRef?.current?.doReset()}
                    >
                        <Icon>filter_alt_off</Icon>
                    </IconButton>
                </Tooltip>
            </MuiGrid>
        </MuiGrid>
    );
};

const TotalsContent = (props) => {
    const { totals } = props;
    const { t } = useTranslation();
    const { currentLanguage } = useBaseAppContext();

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 5,
                mt: 1,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("operacio.totals.operacions")}
                </Typography>
                <Typography>
                    {numberFormat(
                        totals.totalOperacions,
                        null,
                        currentLanguage,
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("operacio.totals.efectiu")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.importEfectiu,
                        "eur",
                        null,
                        currentLanguage,
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("operacio.totals.targeta")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.importTargeta,
                        "eur",
                        null,
                        currentLanguage,
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("operacio.totals.total")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.importTotal,
                        "eur",
                        null,
                        currentLanguage,
                    )}
                </Typography>
            </Box>
        </Box>
    );
};

const springFilterBuilder = (data) => {
    const { and, equal, ge, le, exists, like } = SpringFilterQueryBuilder;

    const filter = and(
        equal("tipusOperacio", data?.tipusOperacio),
        ge("data", data?.dataInici ? data?.dataInici + "T00:00:00" : undefined),
        le("data", data?.dataFi ? data?.dataFi + "T23:59:59" : undefined),
        equal("embarcacio.id", data?.embarcacio?.id),
        equal("ruta.recurs.id", data?.recurs?.id),
        equal("estatEveret", data?.estatEveret),
        like("numero", likeAny(data?.numero)),
        data?.errorEveret
            ? equal("errorEveret", data?.errorEveret.toString())
            : undefined,
        data?.article &&
            exists(equal("liniesOperacio.article.id", data?.article?.id)),
    ).toString();

    return filter;
};

const OperacioFilter = (props) => {
    const { filterApiRef, onSpringFilterChange, onFilterInitialized } = props;

    return (
        <Box sx={{ mb: 2 }}>
            <Filter
                name={"O_FILTER"}
                resourceName={"operacio"}
                persistentState
                springFilterBuilder={springFilterBuilder}
                onSpringFilterChange={onSpringFilterChange}
                onFilterInitialized={onFilterInitialized}
                size="small"
                apiRef={filterApiRef}
                initialOnChangeRequest
            >
                <FilterContent filterApiRef={filterApiRef} />
            </Filter>
        </Box>
    );
};

const Operacio = () => {
    const { t } = useTranslation();
    const [fixedFilter, setFixedFilter] = React.useState();
    const [filterInitialized, setFilterInitialized] = React.useState(false);
    const gridApiRef = React.useRef();
    const filterApiRef = React.useRef();
    const { currentLanguage } = useBaseAppContext();
    const { apiInitialized, exec: apiExec } = useResourceApi("operacio");
    const [totals, setTotals] = React.useState();
    const [noTraspassats, setNoTraspassats] = React.useState();
    const [isNoTraspassats, setIsNoTraspassats] = React.useState(false);
    const [refreshInitialDataCount, setRefreshInitialDataCount] =
        React.useState(0);
    const [selectionModel, setSelectionModel] = React.useState([]);
    const navigate = useNavigate();

    const { and, notEqual, le, or, isNull } = SpringFilterQueryBuilder;

    const dataLimit = new Date();
    dataLimit.setDate(
        dataLimit.getDate() - noTraspassats?.diesAbansTraspasAutomatic,
    );
    const filterNoTraspassats = isNoTraspassats
        ? and(
              or(
                  isNull("estatEveret"),
                  notEqual("estatEveret", "PAGAMENT_CREAT"),
              ),
              le("data", dataLimit.toISOString().split("T")[0] + "T23:59:59"),
          )?.toString()
        : undefined;

    const activeFilters = [fixedFilter, filterNoTraspassats].filter(Boolean);
    const fixedFilterWithNoTraspassats =
        activeFilters.length > 0 ? activeFilters.join(" AND ") : undefined;

    // Hi ha aquesta useEffect perque quan es fa click al boto de no traspassats, es refresca el filtre inicial i es lleven tots el filtres
    // i es reinicia el filtre amb el nou filtre de no traspassats
    // Es fa un contador i no un boolea, perque si es fa click molt ràpidament el boolea pot quedar "igual" i no refrescar el filtre,
    // en canvi amb el contador sempre canvia i refresca el filtre
    React.useEffect(() => {
        if (refreshInitialDataCount > 0) {
            filterApiRef?.current?.doReset();
        }
    }, [refreshInitialDataCount]);

    const columns = [
        {
            name: "data",
            flex: 0.75,
            minWidth: 175,
        },
        {
            name: "tipusOperacio",
            flex: 1,
            minWidth: 150,
        },
        {
            name: "numero",
            flex: 0.75,
            minWidth: 120,
        },
        {
            name: "ruta",
            flex: 1,
            minWidth: 250,
        },
        {
            name: "embarcacio",
            flex: 1,
            minWidth: 150,
        },
        {
            name: "user",
            flex: 1,
            minWidth: 150,
        },
        {
            name: "numeroLinies",
            flex: 0.75,
            minWidth: 100,
        },
        {
            name: "estatEveret",
            flex: 2,
            minWidth: 160,
        },
        {
            name: "importEfectiu",
            flex: 0.5,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "importTargeta",
            flex: 0.5,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "importBase",
            flex: 2.5,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "quotaIva",
            flex: 2.5,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "importTotal",
            flex: 2.5,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
    ];

    const toolbarAdditionalActions = () => {
        const listActions = [
            {
                position: 2,
                component: (
                    <Tooltip
                        title={
                            isNoTraspassats
                                ? t("operacio.noTraspassats.aplicat")
                                : t("operacio.noTraspassats.noAplicat")
                        }
                    >
                        <IconButton
                            color={isNoTraspassats ? "warning" : undefined}
                            onClick={() => {
                                setIsNoTraspassats(!isNoTraspassats);
                                if (!isNoTraspassats) {
                                    setRefreshInitialDataCount(
                                        refreshInitialDataCount + 1,
                                    );
                                }
                            }}
                        >
                            <Badge
                                badgeContent={noTraspassats?.totalNoTraspassats}
                                color="info"
                                max={999}
                                sx={{
                                    "& .MuiBadge-badge": {
                                        height: 16,
                                        minWidth: 16,
                                        fontSize: "0.7rem",
                                        padding: "0 6px",
                                    },
                                }}
                            >
                                <ReportProblemIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>
                ),
            },
            {
                position: 2,
                component: (
                    <ActionButton
                        resourceName="operacio"
                        code="O_SELECCIONAR_TOT"
                        title={t("operacio.action.seleccionarTot")}
                        detached
                        variant="contained"
                        icon="checklist"
                        formDialogSize="lg"
                        ignoreFields
                        confirmed
                        formStaticData={{
                            springFilter: fixedFilterWithNoTraspassats,
                        }}
                        onSuccess={(params) => {
                            setSelectionModel(
                                params.map((value) => parseInt(value)),
                            );
                        }}
                    />
                ),
            },
            {
                position: 2,
                component: (
                    <IconButton
                        title={t("comu.deselecionar")}
                        onClick={() => gridApiRef?.current?.doSelectNone()}
                        disabled={selectionModel?.length === 0}
                    >
                        <Icon>playlist_remove</Icon>
                    </IconButton>
                ),
            },
            {
                position: 2,
                component: (
                    <ActionButton
                        code="O_ACCIO_FACTURAR_I_PAGAR"
                        resourceName="operacio"
                        multiple
                        icon="paid"
                        variant="contained"
                        onSuccess={() => {
                            gridApiRef?.current?.doRefresh();
                        }}
                    />
                ),
            },
        ];

        return listActions;
    };

    const rowAdditionalActions = (params) => {
        const isTipusOperacioValid = [
            "VENDA",
            "FOTOGRAFIA",
            "CONSUM_TRIPULACIO",
        ].includes(params?.tipusOperacio);
        const noPotFacturar = ["FACTURA_CREADA", "PAGAMENT_CREAT"].includes(
            params?.estatEveret,
        );

        const potEfectuarPagament = params?.estatEveret === "FACTURA_CREADA";

        const listActions = [
            {
                icon: "search", //visibility o search
                label: t("comu.detall"),
                onClick: () => navigate(`form/${params?.id}`),
            },
            // TODO: Descomentar si es vol tornar a activar l'acció d'abonar operacions
//             {
//                 icon: "currency_exchange",
//                 showInMenu: true,
//                 rowLinkAction: "O_ABONAR",
//                 rowLinkActionOnSuccess: () => gridApiRef?.current?.doRefresh(),
//                 rowLinkFormChildren: <ActionAbonarContent operacio={params} />,
//                 rowLinkFormStaticData: { operacioId: params.id },
//                 rowLinkFormDialogSize: "lg",
//                 rowLinkFormInitialOnChangeRequest: true,
//                 disabled: !isTipusOperacioValid,
//             },
            {
                icon: "receipt_long",
                showInMenu: true,
                rowLinkAction: "O_FACTURAR",
                disabled: !isTipusOperacioValid || noPotFacturar,
            },
            {
                icon: "payments",
                showInMenu: true,
                rowLinkAction: "O_EFECTUAR_PAGAMENT",
                rowLinkActionIgnoreFields: true,
                disabled: !(potEfectuarPagament && isTipusOperacioValid),
            },
        ];

        return listActions;
    };

    // Todo: S'ha posat aixo pq la llibreria no refresca la traduccio de l'accio sense refrescar el grid
    React.useEffect(() => {
        gridApiRef?.current?.doRefresh();
    }, [currentLanguage]);

    const obtenirNoTraspassats = () => {
        if (apiInitialized) {
            apiExec("exec_O_OBTENIR_NO_TRASPASSATS", {
                callbacks: {
                    setResponse: (response) => {
                        setNoTraspassats(response.data);
                    },
                    setError: (error) => {},
                },
            });
        }
    };

    const obtenirTotals = () => {
        apiExec("exec_O_OBTENIR_TOTALS", {
            data: { springFilter: fixedFilterWithNoTraspassats ?? "" }, // TODO: Ha de poder rebre undefined com a filtre o text buid
            callbacks: {
                setResponse: (response) => {
                    const resource = {
                        ...response.data,
                        _resource: response,
                        _links: response.links,
                    };
                    setTotals(resource);
                },
                setError: (error) => {},
            },
        });
    };

    React.useEffect(() => {
        if (apiInitialized && filterInitialized) {
            obtenirTotals();
            obtenirNoTraspassats();
        }
    }, [
        apiInitialized,
        filterInitialized,
        fixedFilterWithNoTraspassats,
    ]);

    const dataGridCustomStyles = {
        "& .cellAbonat, & .cellEntradaManual": {
            bgcolor: (theme) => getFielYellow(theme.palette.mode),
        },
        "& .MuiDataGrid-row:hover .cellAbonat, & .MuiDataGrid-row:hover .cellEntradaManual":
            {
                bgcolor: (theme) =>
                    `${darken(
                        getFielYellow(theme.palette.mode),
                        0.05,
                    )}!important`,
            },
        "& .cellAbonament": {
            bgcolor: (theme) => getFieldHighlightRed(theme.palette.mode),
            color: "#ab0000",
        },
        "& .MuiDataGrid-row:hover .cellAbonament": {
            bgcolor: (theme) =>
                `${darken(
                    getFieldHighlightRed(theme.palette.mode),
                    0.05,
                )}!important`,
        },
        "& .cellErrorEveret": {
            bgcolor: (theme) => getFieldHighlightRed(theme.palette.mode),
        },
        "& .MuiDataGrid-row:hover .cellErrorEveret": {
            bgcolor: (theme) =>
                `${darken(
                    getFieldHighlightRed(theme.palette.mode),
                    0.1,
                )}!important`,
        },
    };

    return (
        <Box
            sx={{
                height: "100%",
                ...dataGridCustomStyles,
            }}
        >
            <Grid
                apiRef={gridApiRef}
                title={t("operacio.title")}
                resourceName="operacio"
                fixedFilter={fixedFilterWithNoTraspassats}
                findDisabled={!filterInitialized}
                actionExecEnabled
                toolbarHideQuickFilter
                onRefresh={() => obtenirTotals()}
                onDelete={() => obtenirTotals()}
                rowHideEditViewAction
                rowAdditionalActions={(params) =>
                    rowAdditionalActions(params.row)
                }
                toolbarAdditionalActions={toolbarAdditionalActions()}
                columns={columns}
                selectable
                selectionModel={selectionModel}
                onSelectionChange={setSelectionModel}
                formRoutePath="form"
                density="compact"
                toolbarAdditionalRow={
                    <>
                        <OperacioFilter
                            filterApiRef={filterApiRef}
                            onSpringFilterChange={setFixedFilter}
                            onFilterInitialized={() =>
                                setFilterInitialized(true)
                            }
                        />
                        {totals && (
                            <Box sx={{ mb: 2 }}>
                                <TotalsContent totals={totals} />
                            </Box>
                        )}
                    </>
                }
                initialState={{
                    pinnedColumns: {
                        left: [GRID_CHECKBOX_SELECTION_FIELD],
                        right: ["importTotal", "actions"],
                    },
                }}
                getCellClassName={({ row, field }) => {
                    // Pinta les caselles de les operacions que son abonaments totals/parcials
                    if (
                        row?.abonamentTipus === "ABONAMENT_TOTAL" ||
                        row?.abonamentTipus === "ABONAMENT_PARCIAL"
                    ) {
                        if (field === "importTotal" && row?.importTotal < 0) {
                            return "cellAbonament";
                        }
                        if (
                            field === "importEfectiu" &&
                            row?.importEfectiu < 0
                        ) {
                            return "cellAbonament";
                        }
                        if (
                            field === "importTargeta" &&
                            row?.importTargeta < 0
                        ) {
                            return "cellAbonament";
                        }
                    }
                    if (row?.errorEveret) {
                        return "cellErrorEveret";
                    }
                    // Pinta la linia de l'operacio original que ha estat total/parcialment abonada
                    if (
                        row?.abonamentTipus === "OPERACIO_ABONADA_PARCIAL" ||
                        row?.abonamentTipus === "OPERACIO_ABONADA_TOTAL"
                    ) {
                        return "cellAbonat";
                    }
                    if (row?.entradaManual || row?.liniaManual) {
                        return "cellEntradaManual";
                    }

                    return "";
                }}
                defaultSortModel={[
                    {
                        field: "data",
                        sort: "desc",
                    },
                ]}
            />
        </Box>
    );
};

export default Operacio;
